import { Box, Button, Stack, TextField } from "@mui/material";
import { LoadingComponent } from "@gui/widgets/LoadingComponent";
import { AsyncStatus } from "@gui/widgets/A11y";
import { PageLayout } from "@gui/widgets/PageLayout";
import { LoginProps } from "./login.types";
import { useLoginViewModel } from "./login.vm";

export function LoginForm(props: LoginProps) {
	const vm = useLoginViewModel(props);

	// The auth pages' shared shape (PageLayout `form` width): fields, the main action full width, then the
	// secondary links.
	return (
		<PageLayout width="form">
			<Stack spacing={2} data-testid="login-form" component="form" onSubmit={vm.handleSubmit} noValidate>
				<TextField
					variant="outlined"
					required
					error={!!vm.emailError}
					disabled={vm.loading}
					fullWidth
					id="email"
					label="Email"
					name="email"
					autoComplete="email"
					autoFocus
					value={vm.username}
					onChange={vm.handleEmailChange}
					data-sentry-component="Email Field"
					// On the *inner* <input>, not TextField's wrapper — a testid on the root would
					// resolve to a div that Playwright cannot fill().
					slotProps={{ htmlInput: { "data-testid": "login-email" } }}
				/>
				<TextField
					variant="outlined"
					error={!!vm.passwordError}
					required
					disabled={vm.loading}
					fullWidth
					name="password"
					label="Password"
					type="password"
					id="password"
					autoComplete="current-password"
					value={vm.password}
					onChange={vm.handlePasswordChange}
					data-sentry-component="Password Field"
					slotProps={{ htmlInput: { "data-testid": "login-password" } }}
				/>
				<Button
					type="submit"
					disabled={vm.isSubmitDisabled}
					fullWidth
					variant="contained"
					size="large"
					sx={{ mt: 1 }}
					data-sentry-component="Submit Button"
					data-testid="login-submit"
				>
					Sign In
				</Button>
				{/* No form-level error element here by design (#661). Every sign-in failure — the three
			    known business errors and the unknown/network path alike — is surfaced by
			    `Notifications.error` / `Oops.handleError`, which toast with their own role="alert".
			    The old element was bound to a `setError` that was only ever called with "", so it
			    could never paint. Toasts own login failure; if that ever changes, the message needs
			    to move back here rather than a dead element being kept warm for it. */}
				{vm.loading && (
					<Box display="flex" justifyContent="center" alignItems="center" aria-busy="true">
						<AsyncStatus state="loading" loadingText="Signing in…" />
						<LoadingComponent />
					</Box>
				)}
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<Button
						disabled={vm.loading}
						onClick={vm.navigateToForgotPassword}
						variant="text"
						data-sentry-component="Forgot Password Button"
					>
						Forgot Password?
					</Button>
					<Button
						disabled={vm.loading}
						onClick={vm.navigateToRegister}
						variant="outlined"
						data-sentry-component="Register Button"
					>
						Register
					</Button>
				</Box>
			</Stack>
		</PageLayout>
	);
}
